import { routeIntersectsAnyRect, type SpatialRect } from '../../../core/layout/spatialIntent';

export interface LayoutPointRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const clampNumber = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export const layoutEndpointTouchesNode = (
  point: { x: number; y: number },
  rect: LayoutPointRect,
  tolerance = 16,
): boolean => {
  const closestX = clampNumber(point.x, rect.x, rect.x + rect.width);
  const closestY = clampNumber(point.y, rect.y, rect.y + rect.height);
  return Math.hypot(point.x - closestX, point.y - closestY) <= tolerance;
};

export const reverseFlatLayoutPoints = (points: number[]): number[] => {
  const reversed: number[] = [];
  for (let i = points.length - 2; i >= 0; i -= 2) {
    reversed.push(points[i] as number, points[i + 1] as number);
  }
  return reversed;
};

const layoutPointToleranceForSource = (layoutSource?: string): number =>
  layoutSource === 'compass-explorer' || layoutSource === 'compose-service' ? 1000 : 8;

export const resolveEffectiveLayoutPoints = (
  layoutPoints: number[],
  layoutSource: string | undefined,
  _fromPoint: { x: number; y: number },
  _toPoint: { x: number; y: number },
  fromRect: LayoutPointRect | null,
  toRect: LayoutPointRect | null,
): number[] => {
  if (layoutPoints.length < 4) return layoutPoints;
  const firstLayoutPoint = { x: layoutPoints[0] as number, y: layoutPoints[1] as number };
  const lastLayoutPoint = {
    x: layoutPoints[layoutPoints.length - 2] as number,
    y: layoutPoints[layoutPoints.length - 1] as number,
  };
  const touchesForward = Boolean(
    layoutSource === 'elk' &&
    fromRect &&
    toRect &&
    layoutEndpointTouchesNode(firstLayoutPoint, fromRect) &&
    layoutEndpointTouchesNode(lastLayoutPoint, toRect),
  );
  const touchesReversed = Boolean(
    layoutSource === 'elk' &&
    fromRect &&
    toRect &&
    layoutEndpointTouchesNode(firstLayoutPoint, toRect) &&
    layoutEndpointTouchesNode(lastLayoutPoint, fromRect),
  );
  if (touchesReversed && !touchesForward) {
    return reverseFlatLayoutPoints(layoutPoints);
  }
  return layoutPoints;
};

export const layoutPointsMatchEndpoints = (
  layoutPoints: number[],
  fromPoint: { x: number; y: number },
  toPoint: { x: number; y: number },
  layoutSource: string | undefined,
  fromRect: LayoutPointRect | null,
  toRect: LayoutPointRect | null,
): boolean => {
  if (layoutPoints.length < 4) return false;
  const tolerance = layoutPointToleranceForSource(layoutSource);
  const startDx = (layoutPoints[0] as number) - fromPoint.x;
  const startDy = (layoutPoints[1] as number) - fromPoint.y;
  const endDx = (layoutPoints[layoutPoints.length - 2] as number) - toPoint.x;
  const endDy = (layoutPoints[layoutPoints.length - 1] as number) - toPoint.y;
  const exactMatch =
    Math.hypot(startDx, startDy) <= tolerance && Math.hypot(endDx, endDy) <= tolerance;
  if (exactMatch) return true;
  if (layoutSource !== 'elk' || !fromRect || !toRect) return false;
  const firstLayoutPoint = { x: layoutPoints[0] as number, y: layoutPoints[1] as number };
  const lastLayoutPoint = {
    x: layoutPoints[layoutPoints.length - 2] as number,
    y: layoutPoints[layoutPoints.length - 1] as number,
  };
  const touchesForward =
    layoutEndpointTouchesNode(firstLayoutPoint, fromRect) &&
    layoutEndpointTouchesNode(lastLayoutPoint, toRect);
  const touchesReversed =
    layoutEndpointTouchesNode(firstLayoutPoint, toRect) &&
    layoutEndpointTouchesNode(lastLayoutPoint, fromRect);
  return touchesForward || touchesReversed;
};

export interface EvaluatePrecomputedLayoutHonorInput {
  layoutPoints: number[];
  fromPoint: { x: number; y: number };
  toPoint: { x: number; y: number };
  fromRect: LayoutPointRect | null;
  toRect: LayoutPointRect | null;
  layoutSource?: string;
  primaryReportsBelowParent: boolean;
  /** A left/right layout may put a child below its parent on screen. */
  isHorizontalOrgLayout?: boolean;
  shouldPreferOrgBusOverPrecomputedRoute: boolean;
  obstacleRects: SpatialRect[];
  layoutSourceAllowedByPolicy: boolean;
  validateVisibleGeometry: boolean;
}

export const evaluatePrecomputedLayoutHonor = (
  input: EvaluatePrecomputedLayoutHonorInput,
): { canHonor: boolean; canHonorIgnoringBusPreference: boolean; effectivePoints: number[] } => {
  const {
    layoutPoints,
    fromPoint,
    toPoint,
    fromRect,
    toRect,
    layoutSource,
    primaryReportsBelowParent,
    isHorizontalOrgLayout = false,
    shouldPreferOrgBusOverPrecomputedRoute,
    obstacleRects,
    layoutSourceAllowedByPolicy,
    validateVisibleGeometry,
  } = input;

  const effectivePoints = resolveEffectiveLayoutPoints(
    layoutPoints,
    layoutSource,
    fromPoint,
    toPoint,
    fromRect,
    toRect,
  );

  const endpointsMatch = layoutPointsMatchEndpoints(
    layoutPoints,
    fromPoint,
    toPoint,
    layoutSource,
    fromRect,
    toRect,
  );

  const crossesObstacle =
    endpointsMatch &&
    effectivePoints.length >= 4 &&
    routeIntersectsAnyRect(effectivePoints, obstacleRects);

  const layoutPointsTrustVisibleGeometry = !validateVisibleGeometry || !crossesObstacle;

  const canHonorIgnoringBusPreference =
    layoutPoints.length >= 4 &&
    endpointsMatch &&
    layoutSourceAllowedByPolicy &&
    layoutPointsTrustVisibleGeometry;

  const canHonor = canHonorIgnoringBusPreference &&
    !shouldPreferOrgBusOverPrecomputedRoute &&
    (!primaryReportsBelowParent || isHorizontalOrgLayout);

  return { canHonor, canHonorIgnoringBusPreference, effectivePoints };
};
